import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { format } from 'date-fns';
import { 
  Activity, 
  Users, 
  Star, 
  Heart, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  MapPin
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface PerformanceSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: string;
  guardId: string;
  data?: {
    patrols: any[];
    emergencyReports: any[];
    supervisorReports: any[];
    clientEvaluations: any[];
    behaviorRecords: any[];
    clockRecords: any[];
  };
}

export const PerformanceSourceModal: React.FC<PerformanceSourceModalProps> = ({
  isOpen,
  onClose,
  metricType,
  guardId,
  data
}) => {
  const { t } = useLanguage();
  
  // Helper function to translate behavior names
  const translateBehaviorName = (name: string | undefined) => {
    if (!name) return '';
    
    // Define behavior name translations
    const behaviorTranslations: { [key: string]: string } = {
      'Απρεπής Συμπεριφορά': t('inappropriateBehavior') || 'Inappropriate Behavior',
      'Εγκατάλειψη Θέσης': t('postAbandonment') || 'Post Abandonment',
      'Καθυστέρηση': t('delay') || 'Delay',
      'Απουσία': t('absence') || 'Absence',
      'Θετική Αναφορά': t('positiveReport') || 'Positive Report',
      'Αρνητική Αναφορά': t('negativeReport') || 'Negative Report',
      'Έγκαιρη Άφιξη': t('timelyArrival') || 'Timely Arrival',
      'Παρουσία Εντός 15λεπτων': t('presenceWithin15min') || 'Presence Within 15min',
      'Συνέπεια': t('consistency') || 'Consistency',
      'Πρωτοβουλία': t('initiative') || 'Initiative',
      'Συνεργασία': t('cooperation') || 'Cooperation'
    };
    
    return behaviorTranslations[name] || name;
  };
  
  const renderContent = () => {
    switch (metricType) {
      case 'operational':
        return (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Περιπολίες ({data?.patrols?.length || 0})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {data?.patrols?.slice(0, 10).map((patrol) => (
                  <Card key={patrol.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3 text-blue-500" />
                        <span className="text-sm">Περιπολία</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {format(new Date(patrol.created_at), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Lat: {patrol.latitude}, Lng: {patrol.longitude}
                    </div>
                  </Card>
                ))}
                {(data?.patrols?.length || 0) > 10 && (
                  <p className="text-xs text-gray-500 text-center">
                    και {(data?.patrols?.length || 0) - 10} ακόμα...
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Έκτακτες Αναφορές ({data?.emergencyReports?.length || 0})
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {data?.emergencyReports?.map((report) => (
                  <Card key={report.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm">{report.title}</span>
                        <Badge className={`ml-2 ${
                          report.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          report.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {report.severity}
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500">
                        {format(new Date(report.created_at), 'dd/MM/yyyy')}
                      </span>
                    </div>
                    {report.description && (
                      <p className="text-xs text-gray-600 mt-1 truncate">
                        {report.description}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );

      case 'supervisor':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Αναφορές Επιβλεπόντων ({data?.supervisorReports?.length || 0})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.supervisorReports?.map((report) => (
                <Card key={report.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{report.title}</span>
                      <Badge className={`ml-2 ${
                        report.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        report.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                        report.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {report.severity}
                      </Badge>
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(new Date(report.created_at), 'dd/MM/yyyy')}
                    </span>
                  </div>
                  {report.description && (
                    <div className="text-xs text-gray-600 mt-2 space-y-1">
                      {(() => {
                        try {
                          const parsed = typeof report.description === 'string' 
                            ? JSON.parse(report.description) 
                            : report.description;
                          
                          return (
                            <div className="space-y-1">
                              {parsed.report_type && (
                                <div><strong>Type:</strong> {parsed.report_type.replace(/_/g, ' ')}</div>
                              )}
                              {parsed.weather_conditions && (
                                <div><strong>Weather:</strong> {parsed.weather_conditions.replace(/_/g, ' ')}</div>
                              )}
                              {parsed.security_equipment_status && (
                                <div><strong>Equipment:</strong> {parsed.security_equipment_status}</div>
                              )}
                              {parsed.guard_evaluations && Object.keys(parsed.guard_evaluations).length > 0 && (
                                <div className="mt-2 p-2 bg-blue-50 rounded">
                                  <strong>Guard Evaluations:</strong>
                                  {Object.entries(parsed.guard_evaluations).map(([guardId, evaluation]: [string, any]) => (
                                    <div key={guardId} className="ml-2 mt-1">
                                      {evaluation.performance_rating && (
                                        <div>Performance: {evaluation.performance_rating}/5 ⭐</div>
                                      )}
                                      {evaluation.compliance_status && (
                                        <div>Compliance: {evaluation.compliance_status}/5 ⭐</div>
                                      )}
                                      {evaluation.behavioral_observation && (
                                        <div>Notes: {evaluation.behavioral_observation}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        } catch (e) {
                          return <div>{String(report.description)}</div>;
                        }
                      })()}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );

      case 'client':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Star className="h-4 w-4" />
              Αξιολογήσεις Πελατών ({data?.clientEvaluations?.length || 0})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.clientEvaluations?.map((evaluation) => (
                <Card key={evaluation.id} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">
                      Περίοδος {evaluation.evaluation_period}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(evaluation.submitted_at), 'dd/MM/yyyy')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Συνολική Ικανοποίηση: ⭐ {evaluation.overall_satisfaction}/5</div>
                    <div>Αξιοπιστία: ⭐ {evaluation.reliability_score}/5</div>
                    <div>Επαγγελματισμός: ⭐ {evaluation.professionalism_score}/5</div>
                    <div>Επικοινωνία: ⭐ {evaluation.communication_score}/5</div>
                  </div>
                  {evaluation.additional_comments && (
                    <p className="text-xs text-gray-600 mt-2 italic">
                      "{evaluation.additional_comments}"
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );

      case 'behavior':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Καταγραφές Συμπεριφοράς ({data?.behaviorRecords?.length || 0})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.behaviorRecords?.map((record) => (
                <Card key={record.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {record.behavior_type?.category === 'positive' ? 
                        <CheckCircle className="h-4 w-4 text-green-500" /> :
                        <XCircle className="h-4 w-4 text-red-500" />
                      }
                      <span className="font-medium text-sm">
                        {translateBehaviorName(record.behavior_type?.name)}
                      </span>
                      <Badge className={`${
                        record.behavior_type?.category === 'positive' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {record.score_applied > 0 ? '+' : ''}{record.score_applied} {t('points') || 'πόντοι'}
                      </Badge>
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(new Date(record.incident_date), 'dd/MM/yyyy')}
                    </span>
                  </div>
                  {record.description && (
                    <p className="text-xs text-gray-600 mt-1">
                      {record.description}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );

      case 'punctuality':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Έγκαιρα Check-in ({data?.clockRecords?.length || 0})
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.clockRecords?.map((record) => (
                <Card key={record.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">
                        Check-in: {format(new Date(record.clock_in), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </div>
                    <Badge className={`${
                      record.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : record.status === 'active'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {record.status}
                    </Badge>
                  </div>
                  {record.clock_out && (
                    <div className="text-xs text-gray-600 mt-1">
                      Check-out: {format(new Date(record.clock_out), 'dd/MM/yyyy HH:mm')}
                      {record.auto_checkout && " (Αυτόματο)"}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );

      default:
        return <div>{t('noDataAvailableForMetric') || 'Δεν υπάρχουν διαθέσιμα δεδομένα για αυτό το μετρικό.'}</div>;
    }
  };

  const getTitle = () => {
    switch (metricType) {
      case 'operational': return t('operationalActivity') || 'Επιχειρησιακή Δραστηριότητα';
      case 'supervisor': return t('supervisorReports') || 'Αναφορές Επιβλεπόντων';
      case 'client': return t('clientEvaluations') || 'Αξιολογήσεις Πελατών';
      case 'behavior': return t('behaviors') || 'Συμπεριφορές';
      case 'punctuality': return t('punctualityAndTimely') || 'Συνέπεια & Έγκαιρα Check-in';
      default: return t('details') || 'Λεπτομέρειες';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};